import { environment } from '../../../../../environments/environment';
import { canOpenGestion } from '../../../gestion/gestion-tabs';
import { SAAS_GROUPS } from '../../../login/guards/module-access-policy';
import { AuthService } from '../../../login/services/auth.service';
import { Roles } from '../../../login/models/roles.enum';

export interface SideNavNode {
  name: string;
  icon?: string;
  routerLink?: string;
  permissions?: string[];
  children?: SideNavNode[];
  parent?: SideNavNode;
  enabled?: boolean;
  hide?: boolean | ((authService: AuthService) => boolean);
  hidden?: boolean;
  id?: string;
  devMode?: boolean;
  requiredPermission?: string;
}

function canOpenGestionFromLogin(authService: AuthService): boolean {
  const user = authService.getUserLogin();
  const groups: readonly string[] = Array.isArray(user?.groups)
    ? user.groups
    : [];

  return canOpenGestion(environment.production, groups);
}

const MENU_TREE_DATA: SideNavNode[] = [
  {
    name: 'Inicio',
    icon: 'home',
    routerLink: '/home',
    enabled: true,
    id: 'inicio',
  },
  {
    name: 'Gestión',
    icon: 'description',
    routerLink: '/gestion',
    id: 'gestion',
    hide: authService => !canOpenGestionFromLogin(authService),
  },
  {
    name: '4SIG',
    icon: 'map',
    routerLink: '/4sig',
    id: '4sig',
  },
  {
    name: 'Hidráulica',
    icon: 'water_drop',
    id: 'hidraulica',
    children: [
      {
        name: 'Estado de red',
        routerLink: '/hidraulica/estado-de-red',
      },
      {
        name: 'Gestión de lecturas',
        routerLink: '/hidraulica/gestion-de-lecturas',
      },
      {
        name: 'Informes de riego',
        routerLink: '/hidraulica/informes-de-riego',
      },
      {
        name: 'Eficiencia de red',
        routerLink: '/hidraulica/eficiencia-de-red',
      },
    ],
  },
  {
    name: 'Sensores',
    icon: 'partly_cloudy_day',
    id: 'sensores',
    children: [
      {
        name: 'Métricas',
        routerLink: '/sensores/metricas',
      },
      {
        name: 'Ajustes de métricas',
        routerLink: '/sensores/ajustes-metricas',
        id: 'ajustes_metricas',
        hide: authService => authService.isComunero(),
      },
      {
        name: 'Previsión AEMET',
        routerLink: '/sensores/prevision-aemet',
      },
      {
        name: 'Gestión de estaciones',
        routerLink: '/sensores/gestion-estaciones',
        id: 'gestion_estaciones',
        requiredPermission: 'manage_stations_access',
      },
    ],
  },
  {
    name: 'Teledetección',
    icon: 'satellite_alt',
    id: 'teledeteccion',
    routerLink: '/teledeteccion',
  },
  {
    name: 'Medioambiente',
    id: 'medioambiente',
    icon: 'psychiatry',
    children: [
      {
        name: 'Huella de carbono',
        routerLink: '/medio-ambiente/huella-carbono',
      },
      {
        name: 'Indicadores ambientales',
        routerLink: '/medio-ambiente/indicadores-ambientales',
      },
      {
        name: 'Sellos',
        routerLink: '/medio-ambiente/sellos',
      },
    ],
  },
  {
    name: 'Energía',
    id: 'energia',
    icon: 'bolt',
    children: [
      {
        name: 'Panel de Control',
        routerLink: '/energia/panel-control',
      },
      {
        name: 'Gestión de Dispositivos',
        routerLink: '/energia/gestion-dispositivos',
        hide: authService =>
          !authService.hasAnyUserGroup([Roles.ADMINISTRADOR, ...SAAS_GROUPS]),
      },
    ],
  },
  {
    name: 'Ag. de Precisión',
    id: 'ag-de-precision',
    icon: 'pest_control',
    routerLink: '/sanidad-vegetal/analisis',
  },
  {
    id: 'alarmas',
    name: 'Alarmas',
    icon: 'notifications_active',
    children: [
      {
        name: 'Panel de Alarmas',
        routerLink: '/alarmas/panel-alarmas',
      },
      {
        name: 'Gestión de Alarmas',
        routerLink: '/alarmas/gestion-alarmas',
      },
      {
        name: 'Notificaciones por email',
        routerLink: '/alarmas/notificaciones-email',
      },
    ],
  },
  {
    name: 'Cuaderno de Campo',
    id: 'cuaderno-de-campo',
    icon: 'book_2',
    routerLink: '/cuaderno-de-campo',
  },
  {
    name: 'Integraciones',
    id: 'api-publica',
    icon: 'sync_alt',
    routerLink: '/api-publica',
  },
];

const cloneNode = (node: SideNavNode): SideNavNode => ({
  ...node,
  children: node.children?.map(cloneNode),
});

export const getMenuTreeData = (): SideNavNode[] =>
  MENU_TREE_DATA.map(cloneNode);
